import { XIcon } from 'lucide-react'

import type { ExactLocation } from 'src/features/consumers/schemas/consumer'
import ImageCarousel from 'src/shared/components/ui/miscellaneous/carousel/image-carousel'
import { cn } from 'src/shared/utils/cn'
import { formatCurrency } from 'src/shared/utils/format-currency'

import BCImageGrid from './ui/bc-image-grid'

type BCSpecificLocationItemProps = {
  location: ExactLocation & { placeId: string }
  onRemoveLocation: (placeId: string) => void
  variant?: 'default' | 'large'
  className?: string
}

const usePlaceInfo = () => {
  const place = {
    propertyStreetAddress1: '12109 Bryony Dr',
    propertyCity: 'Roslyn Heights',
    propertyState: 'NY',
    propertyZip: '11554',
    images: [
      'https://media.istockphoto.com/id/2175973016/photo/modern-luxury-home-exterior-at-sunset.jpg?s=612x612&w=0&k=20&c=6D40lfnRxmyM3kW3BOIK45fEA7K2p9uvOOcCTufwJvw=',
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTfMPZOQ7I6XEut2V-O8gOHXncJ-ON8IHqhiQ&s',
      'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQi5FardL_8I6bQ8RwwYHgriIdJjrbxeuAxPg&s',
    ] as string[],
    listingPrice: 1125000,
    bedrooms: 5,
    bathrooms: 3.5,
    squareFeet: 3600,
  } as const

  return { data: place, isLoading: false, isError: false }
}

const InfoBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex grow flex-col items-center justify-center gap-0 rounded-[12px] bg-accent p-5">
      <p className="text-lg font-semibold leading-[160%] tracking-[-0.18px] text-primary">{value}</p>
      <p className="text-sm font-medium leading-[160%] tracking-[-0.13px] text-muted">{label}</p>
    </div>
  )
}

const BCSpecificLocationItem = ({
  location,
  onRemoveLocation,
  variant = 'default',
  className,
}: BCSpecificLocationItemProps) => {
  const { data: placeInfo } = usePlaceInfo()

  if (variant === 'large') {
    return (
      <div
        className={cn(
          'relative flex w-[500px] flex-col gap-6 rounded-[30px] p-6 shadow-[0_1px_35px_0px_rgba(27,48,41,0.07)]',
          className,
        )}
      >
        <button
          className="absolute right-4 top-4 z-10 flex size-[45px] items-center justify-center rounded-full bg-primary text-white"
          onClick={() => onRemoveLocation(location.placeId)}
        >
          <XIcon className="size-6" />
        </button>
        <BCImageGrid images={placeInfo.images} />
        <div className="flex flex-col gap-2">
          <h2 className="text-[24px] font-semibold leading-[125%] tracking-[-0.2px]">
            {location.propertyStreetAddress1}
          </h2>
          <p className="text-base font-medium leading-[125%] tracking-[-0.16px] text-muted">
            {location.propertyCity}, {location.propertyState} {location.propertyZip}
          </p>
        </div>
        <div className="flex flex-row gap-1">
          <InfoBox label="Listing price" value={`$${formatCurrency(placeInfo.listingPrice)}`} />
          <InfoBox label="Beds" value={placeInfo.bedrooms.toString()} />
          <InfoBox label="Baths" value={placeInfo.bathrooms.toString()} />
          <InfoBox label="Sq. Ft." value={formatCurrency(placeInfo.squareFeet)} />
        </div>
      </div>
    )
  }

  const bedText = placeInfo.bedrooms ? `${placeInfo.bedrooms} beds` : null
  const bathText = placeInfo.bathrooms ? `${placeInfo.bathrooms} baths` : null
  const squareFeetText = placeInfo.squareFeet ? `${placeInfo.squareFeet} sqft` : null

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-4 rounded-[30px] p-2.5 pb-5 shadow-[0_1px_35px_0px_rgba(27,48,41,0.07)]',
        className,
      )}
    >
      <button
        className="absolute right-4 top-4 z-10 flex size-[40px] items-center justify-center rounded-full bg-primary text-white"
        onClick={() => onRemoveLocation(location.placeId)}
      >
        <XIcon className="size-5" />
      </button>
      <div className="h-[170px] overflow-hidden rounded-[20px]">
        <ImageCarousel images={placeInfo.images} />
      </div>
      <div className="flex flex-col gap-1 px-1">
        <h2 className="text-[17px] font-semibold leading-[125%] tracking-[-0.2px]">
          ${formatCurrency(placeInfo.listingPrice)}
        </h2>
        <p className="text-[13px] font-medium leading-[125%] tracking-[-0.16px] text-muted">
          {[bedText, bathText, squareFeetText].filter(Boolean).join(' • ')}
        </p>
        <p className="line-clamp-1 break-words text-xs font-medium leading-[125%] tracking-[-0.16px] text-muted">
          {location.propertyStreetAddress1}, {location.propertyCity}, {location.propertyState} {location.propertyZip}
        </p>
      </div>
    </div>
  )
}

export default BCSpecificLocationItem
